import { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Notification } from '@components/shared';
import routes from '@root/routes';
import api from '@shared/api';
import useToggle from '@shared/useToggle';
import { StoreState } from '@root/types';
import { useHistory } from 'react-router-dom';
import actions from '@store/actions/tourbookExternalListingPage';
import useTabs from './useTabs';
import Location from './Location';
import Listing from './Listing';
import Media from './Media';
import { Form, Layout, FormValues } from '..';

type Props = {
  tourbookId: string;
};

const mapState = (state: StoreState, ownProps) => ({
  tourbookId: ownProps.match.params.tourbookId,
});

const mapDispatch = {
  externalListingPageLoad: (tourbookId: string) =>
    actions.tourbookExternalListingPageLoad(tourbookId),
};

const connector = connect(mapState, mapDispatch);
type ReduxProps = ConnectedProps<typeof connector>;

export const NewPage = ({ tourbookId, externalListingPageLoad }: Props & ReduxProps) => {
  const history = useHistory();
  const { t } = useTranslation('tourbook');

  useEffect(() => {
    externalListingPageLoad(tourbookId);
  }, []);

  const goToTourbook = () => {
    history.push(routes.tourbook(tourbookId));
  };

  const { isOnLocation, isOnMedia, isOnListing, goToLocation, goToMedia, goToListing } = useTabs({
    tourbookId,
  });

  const { value: isPromptEnabled, setFalse: disablePrompt } = useToggle(true);
  const onSubmit = async (values: FormValues) => {
    const result = await api.post(routes.api.tourbookExternalListings(tourbookId), {
      ...values,
      // necessary because the default snake casing of addressLine1 is address_line1
      address_line_1: values.addressLine1,
      address_line_2: values.addressLine2,
      photos: values.photos.map(i => ({
        cloudinaryId: i.cloudinaryId,
        floorPlan: i.description === 'Floor Plan',
      })),
    });

    // TODO: need error handling
    if (result) {
      disablePrompt();
      goToTourbook();

      Notification.info({
        title: t('externalListing.created'),
        text: <a href="#newest">{t('externalListing.createdLink')}</a>,
      });
    }
  };

  return (
    <Form onSubmit={onSubmit} isPromptEnabled={isPromptEnabled} tourbookId={tourbookId}>
      <Layout goToTourbook={goToTourbook}>
        {isOnLocation ? <Location goToMedia={goToMedia} /> : null}
        {isOnMedia ? <Media goToLocation={goToLocation} goToListing={goToListing} /> : null}
        {isOnListing ? <Listing goToMedia={goToMedia} /> : null}
      </Layout>
    </Form>
  );
};

export default connector(NewPage);
